/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	OnChanges,
	SimpleChanges,
	Optional,
	Self,
	ChangeDetectorRef,
} from '@angular/core';

import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';

import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { ActualPhase, PlanAccessLevel } from '@touchplan/common';
import { UntypedFormControl, NgControl } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService, ILoggerService } from '@touchplan/logging';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import { PlanAccess } from 'src/app/app-data/models/PlanAccess';

@Component({
	selector: 'ticket-footer',
	templateUrl: './ticket-footer.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('TicketFooterComponent')],
})
export class TicketFooterComponent extends TicketEditBaseComponent implements OnInit, OnChanges {
	// 	for constraints
	// - still use actualPhase, but it doesn't use the interiem states, it just only supported actualized and planned
	@Input() public phaseFormControl: any;
	@Input() public actualPhase: ActualPhase;
	@Input() public showActualizedToggle = true;
	@Input() public showMoreDetailsButton = true;
	@Input() public formDirty: boolean;
	@Input() public formValid: boolean;
	@Input() public actualizedLabel = 'Update Status';
	@Input() public saveLabel = 'Save';
	@Input() public isReadOnly = false;
	@Input() public isPlanRegion = false;
	@Input() public closeButtonVisible = true;

	/**
	 * @Outputs something to signal that one of the buttons was clicked
	 * TBD what the button are and what should get emited.  For now, get
	 * cancel working again.
	 */
	@Output() public canceled: EventEmitter<string> = new EventEmitter();
	@Output() public saved: EventEmitter<string> = new EventEmitter();
	@Output() public onActualChange: EventEmitter<any> = new EventEmitter();

	//copied and pasted from ticket-edit-component for now, api needs work
	get isPlanned() {
		return this.actualPhase === ActualPhase.planned;
	}
	get isStarted() {
		return this.actualPhase === ActualPhase.started;
	}
	get isPlannedPreview() {
		return this.actualPhase === ActualPhase.plannedPreview;
	}
	get isActualizing() {
		return this.actualPhase === ActualPhase.actualizing;
	}
	get isActualized() {
		return this.actualPhase === ActualPhase.actualized;
	}

	public isActualizedControl = new UntypedFormControl(true);

	public PlanAccessLevel = PlanAccessLevel;

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef,
		private logger: DefaultLoggerService
	) {
		super(control, multiWrapper, cd);
	}

	public ngOnInit() {
		this.isActualizedControl.valueChanges.pipe(takeUntil(this.shutdownSubject)).subscribe((isActualized) => {
			if (this.showActualizedToggle) {
				this.switchChanged(isActualized);
			}
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.showActualizedToggle) {
			if (changes.showActualizedToggle.currentValue) {
				this.isActualizedControl.enable();
			} else if (changes.showActualizedToggle.currentValue === false) {
				this.isActualizedControl.disable();
			}
		}
	}

	public actualizeClicked($event) {
		if (this.isReadOnly) {
			return false;
		}
		this.onActualChange.emit(ActualPhase.actualizing);
	}

	public switchChanged(isActualized: boolean): void {
		if (isActualized) {
			this.logger.log('Emitting', ActualPhase.actualized);
			this.onActualChange.emit(ActualPhase.actualized);
			analyticsEventsService.togglePlanActual(ActualPhase.actualized);
		} else {
			this.logger.log('Emitting', ActualPhase.plannedPreview);
			this.onActualChange.emit(ActualPhase.plannedPreview);
			analyticsEventsService.togglePlanActual(ActualPhase.plannedPreview);
		}
	}

	public cancelClicked() {
		this.canceled.emit();
	}

	public save() {
		this.saved.emit();
	}
}
